import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

export const useNeedsByServiceTypeData = (
  group,
  search,
  causeAndActionReportData
) => {
  const navigate = useNavigate();
  const prevGroupRef = useRef();

  const processGroupData = useCallback(() => {
    const prev = prevGroupRef.current || {};
    const prevGroupJson = JSON.stringify(prev.group);
    const currentGroupJson = JSON.stringify(group);

    if (prevGroupJson === currentGroupJson && search === prev.search) {
      return null;
    }

    prevGroupRef.current = { group, search };

    if (!group) return [];

    const filteredItems = group
      .filter((item) => {
        const matchesSearch =
          item.referenceCode?.toLowerCase().includes(search.toLowerCase()) ||
          item.serviceType?.toLowerCase().includes(search.toLowerCase()) ||
          item.parameterName?.toLowerCase().includes(search.toLowerCase());

        return matchesSearch;
      })
      .map((item) => ({
        className: `${item.parameterName} (${item.referenceCode})`,
        attributes: [
          {
            parameterCode: item.parameterCode,
            parameterName: item.parameterName,
            referenceCode: item.referenceCode,
            reportGroup: item.reportGroup,
            serviceType: item.serviceType,
            networkAjustmentFactor: item.networkAjustmentFactor * 100,
            availableProvidersCity: Math.ceil(item.availableProvidersCity),
            availableProvidersCityWithoutAdjustment: Math.ceil(
              item.availableProvidersCity / item.networkAjustmentFactor
            ),
            expectedProvidersNeedsCity: item.expectedProvidersNeedsCity,
            isCauseAndActionCreated: causeAndActionReportData.some(
              (data) =>
                data.attribute.attribute.referenceCode === item.referenceCode
            ),
          },
        ],
      }));

    return filteredItems;
  }, [group, search, causeAndActionReportData]);

  const createNavigationHandlers = (city, state, cityObj) => {

    const handleAdd = (item) => {
      const networkSufficiencyResult =
        (Math.ceil(item.availableProvidersCity) ||
          item.availableProvidersCityWithoutAdjustment) -
        Math.ceil(item.expectedProvidersNeedsCity);

      const data = {
        referenceCode: item.referenceCode,
        parameterName: item.parameterName,
        serviceType: item.serviceType,
        networkSufficiency:
          networkSufficiencyResult < 0
            ? "Insuficiência de Rede"
            : "Rede Excedente",
        city,
        state,
        cityObj,
      };

      const returnContext = {
        selectedCity: city,
        cityObj: cityObj,
        reportType: 'needsByServiceType',
        selectedStep: {
          name: "Leitos Hospitalares",
          customName: "Dimensionamento por Especialidades"
        },
        currentPath: '/reports'
      };

      if (item.isCauseAndActionCreated) {
        const causeAndActionData = causeAndActionReportData.filter(
          (data) =>
            data.attribute.attribute.referenceCode === item.referenceCode
        );

        navigate("/reports", {
          state: {
            selectedStep: {
              name: "Leitos Hospitalares",
              customName: "Gestão de Rede",
              rowData: data,
              causeAndActionRowData: causeAndActionData,
            },
            returnContext: returnContext
          },
        });
      } else {
        navigate("/reports", {
          state: {
            selectedStep: {
              name: "Leitos Hospitalares",
              customName: "Gestão de Rede",
              rowData: data,
            },
            returnContext: returnContext
          },
        });
      }
    };

    const handleView = (item) => {
      const data = causeAndActionReportData.filter(
        (data) => data.attribute.attribute.referenceCode === item.referenceCode
      );

      const returnContext = {
        selectedCity: city,
        cityObj: cityObj,
        reportType: 'needsByServiceType',
        selectedStep: {
          name: "Leitos Hospitalares",
          customName: "Dimensionamento por Especialidades"
        },
        currentPath: '/reports'
      };

      navigate("/reports", {
        state: {
          selectedStep: {
            name: "Leitos Hospitalares",
            customName: "Gestão de Rede",
            causeAndActionRowData: data,
            editExistent: true,
            viewOnly: true,
          },
          returnContext: returnContext
        },
      });
    };

    const handleEdit = (item) => {
      const data = causeAndActionReportData.filter(
        (data) => data.attribute.attribute.referenceCode === item.referenceCode
      );

      const returnContext = {
        selectedCity: city,
        cityObj: cityObj,
        reportType: 'needsByServiceType',
        selectedStep: {
          name: "Leitos Hospitalares",
          customName: "Dimensionamento por Especialidades"
        },
        currentPath: '/reports'
      };

      navigate("/reports", {
        state: {
          selectedStep: {
            name: "Leitos Hospitalares",
            customName: "Gestão de Rede",
            causeAndActionRowData: data,
            editExistent: true,
          },
          returnContext: returnContext
        },
      });
    };

    return { handleAdd, handleView, handleEdit };
  };

  return { processGroupData, createNavigationHandlers };
};
